import { create } from "zustand"
import { useRef, FC, useMemo, useEffect } from 'react'
import { Modal } from 'bootstrap'
import { Formik, Form, FormikValues, FormikProps } from "formik"
import * as Yup from 'yup'

import { StatusErrorMessage, FormikTextField, FormikMultiFileUpload } from '../widgets'
import { SubmitButtonRow } from '../layout'

import { cloneDeep } from 'lodash'
import { processNoteValues } from "../helpers"
import { IWebFileAttachment } from "../../modules/core/applications"

interface INoteDialogModel  {    
  note: string,
  noteAttachments: Array<IWebFileAttachment>
  hasNote?: 'yes' | 'required' 
  hasAttachment?: 'yes' | 'required'    
}

const initNoteDialogValues: INoteDialogModel = {
  note: '',
  noteAttachments: [],  
  hasNote: undefined,
  hasAttachment: undefined,  
}

const NoteDialogSchema: any = Yup.object({
  hasNote: Yup.string().trim(),
  hasAttachment: Yup.string().trim(),  
  note: Yup.string().trim().label('Note')
  .when('hasNote', {
    is: (val: string) => (val === 'required'),
    then: (schema) => schema.required(),
  }),          
  noteAttachments: Yup.array()
  .when('hasAttachment', {
    is: (val: string) => (val === 'required'),
    then: (schema) => schema.min(1, 'Attachment/s required').required(),
  }),        
});



const ModalNoteDialogForm: FC<FormikProps<INoteDialogModel>> = ({ values, status }) => {
  
  const { toggleShow, cancelText, submitText} = useModalNoteDialogState(state => state);

  return(
  <Form id='kt_modal_note_dialog_form' className='form' noValidate>
    <div className='d-flex flex-column p-4' >
      { (values?.hasNote !== undefined) &&
      <div className="row mb-4">        
        <FormikTextField size="12" label="Note" name="note" rows={3} required={values.hasNote === 'required'} />
      </div>
      }
      { (values?.hasAttachment !== undefined) &&
      <div className="row mb-4">        
        <FormikMultiFileUpload size="12" label="Note Attachment/s" name="noteAttachments" required={values.hasAttachment === 'required'}/>
      </div>    
      }
      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={() => toggleShow(false)} cancelText={cancelText} submitText={submitText} />
    </div>
  </Form>
  )
}


type ModalNoteProps = {
  title: string
  message: string
  onOK: ((values?:any) => void) | undefined
  noteValue?: string  
  onCancel?: Function
  hasNote?: 'yes' | 'required'
  hasAttachment?:  'yes' | 'required'
  submitText?: string
  cancelText?: string
}

interface ModalNoteDialogState {
  title: string
  message: string
  noteValue?: string
  onOK: ((values?:any) => void) | undefined
  onCancel: Function | undefined
  hasNote?: 'yes' | 'required'
  hasAttachment?:  'yes' | 'required'  
  submitText?: string
  cancelText?: string
  show: boolean,
  toggleShow: (show: boolean) => void
  initModal: (params: ModalNoteProps) => void
}

export const useModalNoteDialogState = create<ModalNoteDialogState>()((set, get) => ({
  title: '',
  message: '',
  noteValue: '',
  onOK: undefined,
  onCancel: undefined,
  hasNote: undefined,
  hasAttachment:  undefined,
  acceptFiles: 'ApplicantFileFormats',
  submitText: 'YES',
  cancelText: 'NO',
  show: false,   
  toggleShow: (show: boolean) => { if(get().show !== show) set({show});},
  initModal: (params: ModalNoteProps) => set({
    noteValue: '',
    onCancel: undefined,
    hasNote: undefined,
    hasAttachment:  undefined,    
    submitText: 'YES',
    cancelText: 'NO',
    ...params
  }),
  clear: () => set({ 
    title: '',
    message: '',
    noteValue: '',
    onOK: undefined,
    onCancel: undefined,
    hasNote: undefined,
    hasAttachment:  undefined,
    submitText: 'YES',
    cancelText: 'NO',
    show: false, 
  }),
}));


export const ModalNoteDialog =  () => {
        
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { show, hasNote, hasAttachment, noteValue, onOK, onCancel, message, title, toggleShow} = useModalNoteDialogState(state => state);

  const initValues = useMemo(() => {
    if (show === false) {
      return cloneDeep(initNoteDialogValues);
    }
    let base = {
      ...cloneDeep(initNoteDialogValues),
      hasNote,
      hasAttachment,
    }

    if (noteValue) {
      Object.assign(base, { note: noteValue })
    }

    return(base);        
  }, [show, hasNote, hasAttachment, noteValue]);

    
  const doSubmit = (values: any, actions: FormikValues) => {        
    const { setStatus, setSubmitting } = actions;
    setStatus('');
    
    if (! onOK) {      
      toggleShow(false);
      setSubmitting(false);
      return;
    } 

    setSubmitting(true);
    
    let payload = {};

    processNoteValues(values, payload);
    
    onOK(payload);    
    setSubmitting(false);
    toggleShow(false);
  }

  useEffect(() => {
    if (! (modalRef && modalRef.current)) return;
  
    const modalEle = modalRef.current;    
    if (show) {
      const bsModal = new Modal(modalEle, {
        backdrop: false,
        keyboard: false
      })
      bsModal.show();
      return;                
    } 
    const bsModal= Modal.getInstance(modalEle)
    if (bsModal) bsModal.hide();       
    if (onCancel) onCancel();              
  // eslint-disable-next-line          
  }, [show, modalRef]);

  return (
  <div className="modal" ref={modalRef} id='appModalNoteDialog' data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="modalNoteDialogBackdropLabel" aria-hidden="true">
    <div className="modal-dialog">
      <div className="modal-content">
        <div className="modal-header">
          <h1 className="modal-title fs-5" id="modalNoteDialogBackdropLabel">{title}</h1>
          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => toggleShow(false)}></button>
        </div>
        <div className="modal-body">
          <div className="text-center">
            {message}
          </div>                    
          <Formik
            initialValues={initValues}
            validationSchema={NoteDialogSchema}
            enableReinitialize={true}
            onSubmit={doSubmit}
            component={ModalNoteDialogForm}
          />
        </div>                
      </div>
    </div>
  </div>
  )
};